import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useCart, useCurrency, API } from "../../App";
import { useTheme } from "../../hooks/useTheme";
import UserLayout from "../../components/UserLayout";
import { ChevronRight, ChevronLeft, Wallet, Leaf, X, Star, UtensilsCrossed, Droplets, RefreshCw, ShieldCheck, Egg, Heart } from "lucide-react";
import CobrandingCard from "../../components/CobrandingCard";

export default function UserHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart, getCartQuantityForItem } = useCart();
  const { symbol, logoUrl, homeLogoUrl } = useCurrency();
  const { theme } = useTheme();
  const activeLogoUrl = theme === "dark" ? (homeLogoUrl || logoUrl) : logoUrl;
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customizeItem, setCustomizeItem] = useState(null);
  const [superfoodPosts, setSuperfoodPosts] = useState([]);
  const [nutrition, setNutrition] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);

  useEffect(() => { fetchMenu(); fetchSuperfoodPosts(); fetchNutrition(); fetchRecentReviews(); }, []);

  const fetchRecentReviews = async () => {
    try {
      const res = await fetch(`${API}/reviews/recent`, { credentials: "include" });
      if (res.ok) setRecentReviews(await res.json());
    } catch {}
  };

  const fetchNutrition = async () => {
    try {
      const res = await fetch(`${API}/nutrition/summary`, { credentials: "include" });
      if (res.ok) setNutrition(await res.json());
    } catch {}
  };

  const fetchSuperfoodPosts = async () => {
    try {
      const res = await fetch(`${API}/superfood-blog`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSuperfoodPosts(data || []);
      }
    } catch {}
  };

  const fetchMenu = async () => {
    try {
      const response = await fetch(`${API}/menu`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMenuItems(data);
        setCategories([...new Set(data.map((i) => i.category).filter(Boolean))]);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const bestsellers = menuItems.filter((i) => i.is_bestseller);

  const handleAddToCart = (item) => {
    if ((item.variations?.length > 0) || (item.addon_groups?.length > 0)) {
      setCustomizeItem(item);
    } else {
      addToCart(item);
      toast.success(`${item.name} added`);
    }
  };

  return (
    <UserLayout showHeader={false}>
      <div className="animate-fade-in" data-testid="user-home">
        {/* Wallet Hero */}
        <div
          className="wallet-card mx-4 mt-4"
          data-testid="home-wallet-card"
        >
          <div className="relative z-10">
            <p className="text-[10px] uppercase tracking-widest text-green-100 font-semibold mb-1">Wallet Balance</p>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-4xl font-black" data-testid="home-wallet-balance">{symbol}{Math.round(user?.wallet_balance || 0)}</p>
              <button
                onClick={async (e) => { e.stopPropagation(); const btn = e.currentTarget; btn.classList.add("animate-spin"); await refreshUser(); setTimeout(() => btn.classList.remove("animate-spin"), 600); }}
                className="p-1.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                data-testid="home-refresh-balance"
              >
                <RefreshCw size={14} />
              </button>
            </div>
            <button
              onClick={() => navigate("/wallet")}
              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-5 py-2 text-xs font-bold rounded-full transition-colors"
              data-testid="home-load-credits-btn"
            >
              <Wallet size={14} /> Load Credits
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Cobranding (institutional banner, shown based on customer segments) */}
          <CobrandingCard />

          {/* Greeting */}
          <div className="flex items-center gap-3">
            <img src={activeLogoUrl ? (activeLogoUrl.startsWith("http") ? activeLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${activeLogoUrl}`) : "/supermorsel-logo.jpg"} alt="Super Morsel" className="w-24 h-24 rounded-full object-contain" data-testid="home-logo" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Hey, {user?.name?.split(" ")[0] || "there"}
              </h1>
              <p className="text-base text-gray-500 dark:text-gray-400 mt-1">What are you craving today?</p>
            </div>
          </div>

          {/* Nutrition Speedometers */}
          {nutrition && <NutritionDashboard data={nutrition} />}

          {/* Promotional Banners */}
          <BannerCarousel navigate={navigate} />

          {/* Clean-sourcing trust card — antibiotic-free chicken, free-range eggs */}
          <CleanSourcingCard />

          {/* Superfood Blog Slider */}
          {superfoodPosts.length > 0 && (
            <SuperfoodBlogSlider posts={superfoodPosts} />
          )}

          {/* Cold Press Section */}
          <ColdPressSection />

          {/* Recent Reviews */}
          {recentReviews.length > 0 && <RecentReviews reviews={recentReviews} />}
        </div>
      </div>
    </UserLayout>
  );
}

function BannerCarousel({ navigate }) {
  const [banners, setBanners] = useState([]);
  const [lightbox, setLightbox] = useState(null); // index of open banner

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/home-banners`, { credentials: "include" });
        const data = r.ok ? await r.json() : [];
        if (!cancelled) {
          // Normalise field names so the legacy {src, alt} render path keeps working
          setBanners(data.map((b) => ({ src: b.image_url, alt: b.alt, banner_id: b.banner_id })));
        }
      } catch {
        if (!cancelled) setBanners([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (banners.length === 0) {
    // Empty state — show only the "Browse Menu" tile so the grid never collapses.
    return (
      <div data-testid="banner-carousel">
        <div className="grid grid-cols-2 gap-3">
          <div
            onClick={() => navigate("/menu")}
            className="col-span-2 w-full aspect-[6/4] bg-green-700 hover:bg-green-800 rounded-xl flex items-end p-4 cursor-pointer transition-colors"
            data-testid="browse-menu-card"
          >
            <span className="text-white font-bold text-xl leading-tight">Browse<br/>Menu</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="banner-carousel">
      <div className="grid grid-cols-2 gap-3">
        {banners.map((b, i) => (
          <img
            key={i}
            src={b.src}
            alt={b.alt}
            className="w-full aspect-[15/23] object-cover rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => setLightbox(i)}
            data-testid={`banner-${i}`}
          />
        ))}
        {banners.length % 2 !== 0 && (
          <div
            onClick={() => navigate("/menu")}
            className="w-full aspect-[15/23] bg-green-700 hover:bg-green-800 rounded-xl flex items-end p-4 cursor-pointer transition-colors"
            data-testid="browse-menu-card"
          >
            <span className="text-white font-bold text-xl leading-tight">Browse<br/>Menu</span>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="banner-lightbox"
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg z-10"
              data-testid="close-lightbox"
            >
              <X size={16} className="text-gray-600" />
            </button>
            <img
              src={banners[lightbox].src}
              alt={banners[lightbox].alt}
              className="w-full rounded-xl shadow-2xl"
              data-testid="lightbox-image"
            />
            <p className="text-center text-white text-xs mt-3 font-medium">{banners[lightbox].alt}</p>
            <div className="flex justify-between mt-3">
              <button
                onClick={() => setLightbox((lightbox - 1 + banners.length) % banners.length)}
                className="text-white/70 hover:text-white text-sm font-medium flex items-center gap-1 transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <button
                onClick={() => setLightbox((lightbox + 1) % banners.length)}
                className="text-white/70 hover:text-white text-sm font-medium flex items-center gap-1 transition-colors"
                data-testid="lightbox-next"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CleanSourcingCard() {
  return (
    <section
      className="relative overflow-hidden mx-auto"
      style={{
        maxWidth: "380px",
        background: "#0F2E1D",
        borderRadius: "24px",
        padding: "24px 22px",
        color: "#ffffff",
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
      }}
      data-testid="clean-sourcing-card"
    >
      {/* Background Accent */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: "-40px",
          right: "-40px",
          width: "140px",
          height: "140px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "50%",
        }}
      />

      {/* Top Badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: "#1E5A3A",
          padding: "7px 14px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.6px",
          position: "relative",
          zIndex: 2,
        }}
      >
        🌿 CLEAN PROTEIN PROMISE
      </div>

      {/* Heading */}
      <h2
        style={{
          fontSize: "30px",
          lineHeight: 1.1,
          margin: "18px 0 14px",
          fontWeight: 800,
          position: "relative",
          zIndex: 2,
        }}
      >
        Better Ingredients. <br />
        Better Protein.
      </h2>

      {/* Description */}
      <p
        style={{
          fontSize: "15px",
          lineHeight: 1.65,
          color: "#D6E7DC",
          marginBottom: "24px",
          position: "relative",
          zIndex: 2,
        }}
        data-testid="clean-sourcing-main"
      >
        We only serve responsibly sourced ingredients — because healthy food should actually be healthy.
      </p>

      {/* Feature Cards */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Chicken Card */}
        <div
          style={{
            background: "rgba(255,255,255,0.08)",
            padding: "16px",
            borderRadius: "18px",
            display: "flex",
            gap: "14px",
            alignItems: "flex-start",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              minWidth: "48px",
              height: "48px",
              borderRadius: "14px",
              background: "#1E5A3A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
            }}
          >
            🍗
          </div>
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 700, marginBottom: "5px" }}
              data-testid="clean-sourcing-chicken-heading"
            >
              Antibiotic Residue Free Chicken
            </div>
            <div style={{ fontSize: "13px", lineHeight: 1.55, color: "#CFE3D5" }}>
              Cleaner protein with no unnecessary antibiotics or hidden compromises.
            </div>
          </div>
        </div>

        {/* Eggs Card */}
        <div
          style={{
            background: "rgba(255,255,255,0.08)",
            padding: "16px",
            borderRadius: "18px",
            display: "flex",
            gap: "14px",
            alignItems: "flex-start",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              minWidth: "48px",
              height: "48px",
              borderRadius: "14px",
              background: "#1E5A3A",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
            }}
          >
            🥚
          </div>
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 700, marginBottom: "5px" }}
              data-testid="clean-sourcing-eggs-heading"
            >
              Free Range Eggs
            </div>
            <div style={{ fontSize: "13px", lineHeight: 1.55, color: "#CFE3D5" }}>
              Better living conditions for hens means naturally better quality eggs.
            </div>
          </div>
        </div>
      </div>

      {/* Highlight Strip */}
      <div
        style={{
          marginTop: "22px",
          background: "linear-gradient(135deg,#F4C542,#E8A317)",
          borderRadius: "18px",
          padding: "16px 18px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          boxShadow: "0 8px 20px rgba(232,163,23,0.25)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            minWidth: "44px",
            height: "44px",
            borderRadius: "14px",
            background: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
          }}
        >
          🛡️
        </div>
        <div>
          <div
            style={{
              fontSize: "17px",
              fontWeight: 800,
              color: "#1B1B1B",
              lineHeight: 1.2,
              letterSpacing: "0.2px",
            }}
            data-testid="clean-sourcing-why-heading"
          >
            No shortcuts.
            <span style={{ display: "block" }}>No low-grade sourcing.</span>
          </div>
          <div
            style={{
              marginTop: "4px",
              fontSize: "13px",
              color: "#3A2A00",
              lineHeight: 1.45,
              opacity: 0.9,
            }}
          >
            Just cleaner ingredients and responsibly sourced protein.
          </div>
        </div>
      </div>
    </section>
  );
}


function SuperfoodBlogSlider({ posts }) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === "left" ? -320 : 320, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="superfood-blog-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Leaf size={16} className="text-green-600" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Superfood Lifestyle</h2>
        </div>
        <div className="flex gap-1">
          <button onClick={() => scroll("left")} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="blog-slider-prev">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <button onClick={() => scroll("right")} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="blog-slider-next">
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        {posts.map((post) => (
          <div key={post.post_id} className="min-w-[280px] max-w-[280px] bg-white border border-gray-100 rounded-xl overflow-hidden snap-start flex-shrink-0 shadow-sm flex flex-col" data-testid={`blog-card-${post.post_id}`}>
            {post.image_url ? (
              <img src={post.image_url} alt={post.title} className="w-full aspect-[16/9] object-cover" />
            ) : (
              <div className="w-full aspect-[16/9] bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center">
                <Leaf size={28} className="text-green-300" />
              </div>
            )}
            <div className="p-3 flex flex-col flex-1">
              <h3 className="text-sm font-bold text-gray-900 leading-tight">{post.title}</h3>
              <p className="text-[11px] text-green-600 font-medium mt-0.5">{post.caption}</p>
              {post.content && <p className="text-[11px] text-gray-500 mt-1.5 line-clamp-3 flex-1">{post.content}</p>}
              <a href="/menu" className="inline-block mt-2 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors" data-testid={`blog-order-now-${post.post_id}`}>
                Order Now &rarr;
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ColdPressSection() {
  return (
    <div className="bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl p-6 sm:p-8 border border-green-100" data-testid="cold-press-section">
      <h2 className="text-2xl sm:text-3xl font-light text-gray-900 leading-snug mb-1" style={{ fontFamily: "'Dancing Script', cursive" }}>
        Endorse Cold Press. <span className="text-green-600">Endorse Health.</span>
      </h2>
      <p className="text-xs font-semibold text-green-600 tracking-widest uppercase mb-4">Pure extraction. Powerful nutrition.</p>
      <p className="text-sm text-gray-600 leading-relaxed">
        Cold-pressed oils retain their natural nutrients, antioxidants, and flavor—free from heat and chemicals. It's not just oil, it's a cleaner, smarter choice that supports heart health and elevates everyday meals.
      </p>
    </div>
  );
}


function NutrientGauge({ value, max, label, color, bgColor, unit }) {
  const pct = Math.min((value || 0) / max, 1) * 100;
  const val = Math.round(value || 0);
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[66px] h-[66px]">
        <svg className="w-[66px] h-[66px] -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={radius} stroke={bgColor} strokeWidth="5" fill="none" />
          <circle cx="32" cy="32" r={radius} stroke={color} strokeWidth="5" fill="none"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[11px] font-extrabold text-gray-800 leading-none">{val}</span>
          <span className="text-[8px] text-gray-400 mt-0.5">{unit}</span>
        </div>
      </div>
      <span className="text-[9px] text-gray-500 font-semibold mt-1">{label}</span>
    </div>
  );
}

function NutritionRow({ label, data }) {
  return (
    <div className="min-w-full flex-shrink-0 snap-center px-1">
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-testid={`nutrition-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {/* KCAL container — centered */}
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-2xl py-3 px-4 flex items-center justify-center gap-1.5 mb-3">
          <span className="text-2xl font-extrabold leading-none text-green-700 dark:text-green-300">{Math.round(data.calories || 0)}</span>
          <span className="text-xs font-semibold text-green-600 dark:text-green-400 mb-0.5">kcal</span>
        </div>
        {/* 3 hollow circles with bold green ring, stacked/overlapping, centered */}
        <div className="flex items-center justify-center">
          <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center bg-white border-[4px] border-green-700">
            <span className="text-[11px] font-medium text-green-800 leading-none">Protein</span>
            <span className="text-lg font-extrabold leading-tight mt-1 text-green-800">{Math.round(data.protein || 0)}g</span>
          </div>
          <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center bg-white border-[4px] border-green-500 -ml-5">
            <span className="text-[11px] font-medium text-green-700 leading-none">Carbs</span>
            <span className="text-lg font-extrabold leading-tight mt-1 text-green-700">{Math.round(data.carbs || 0)}g</span>
          </div>
          <div className="w-24 h-24 rounded-full flex flex-col items-center justify-center bg-white border-[4px] border-green-900 -ml-5">
            <span className="text-[11px] font-medium text-green-900 leading-none">Fat</span>
            <span className="text-lg font-extrabold leading-tight mt-1 text-green-900">{Math.round(data.fat || 0)}g</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NutritionDashboard({ data }) {
  const [active, setActive] = useState(0);
  const scrollRef = useRef(null);
  const labels = ["today", "week", "month"];
  const titles = ["Today", "This Week", "This Month"];

  const goTo = (idx) => {
    setActive(idx);
    scrollRef.current?.children[idx]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  return (
    <div data-testid="nutrition-dashboard">
      <p className="text-sm font-bold text-gray-800 mb-3">Your Nutrition Intake</p>
      <div ref={scrollRef} className="flex overflow-x-hidden snap-x snap-mandatory">
        {labels.map((key, i) => (
          <NutritionRow key={key} label={titles[i]} data={data[key]} />
        ))}
      </div>
      <div className="flex justify-center gap-1.5 mt-3">
        {titles.map((t, i) => (
          <button
            key={t}
            onClick={() => goTo(i)}
            className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-all ${i === active ? "bg-green-600 text-white" : "bg-gray-100 text-gray-500"}`}
            data-testid={`nutrition-tab-${labels[i]}`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}


function RecentReviews({ reviews }) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === "left" ? -220 : 220, behavior: "smooth" });
    }
  };

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return (
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={15}
            className={i < full ? "text-amber-400 fill-amber-400" : half && i === full ? "text-amber-400 fill-amber-400/50" : "text-gray-300"}
          />
        ))}
      </div>
    );
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  return (
    <div data-testid="recent-reviews-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star size={16} className="text-amber-500 fill-amber-500" />
          <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wider">What People Say</h2>
        </div>
        <div className="flex gap-1">
          <button onClick={() => scroll("left")} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="reviews-slider-prev">
            <ChevronLeft size={16} className="text-gray-600" />
          </button>
          <button onClick={() => scroll("right")} className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-testid="reviews-slider-next">
            <ChevronRight size={16} className="text-gray-600" />
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
        {reviews.map((review) => (
          <div
            key={review.review_id}
            className="min-w-[200px] max-w-[200px] bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 snap-start flex-shrink-0 flex flex-col"
            data-testid={`review-card-${review.review_id}`}
          >
            <div className="flex items-center justify-between mb-2">
              {renderStars(review.overall_rating)}
              <span className="text-[9px] text-gray-500">{timeAgo(review.created_at)}</span>
            </div>
            <p className="text-xs text-gray-800 font-semibold leading-snug line-clamp-2 mb-1">{review.item_name}</p>
            {review.comment && (
              <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-3 italic flex-1">"{review.comment}"</p>
            )}
            {!review.comment && <div className="flex-1" />}
            <p className="text-[10px] text-emerald-600 mt-2 font-semibold">{review.user_name || "Customer"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
